/**
 * Calendar Helper Functions
 * 
 * Utility functions for calendar operations, date manipulation,
 * event processing, and data transformations.
 */

import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addMonths, addWeeks, addDays, isSameDay } from 'date-fns';
import type { CalendarEvent, CalendarEventAPI, EventGroup, DateRange, CalendarView } from '../types/calendar';

/**
 * Convert API event to frontend CalendarEvent format
 */
export const transformAPIEvent = (apiEvent: CalendarEventAPI): CalendarEvent => {
  return {
    id: apiEvent.id,
    title: apiEvent.title,
    description: apiEvent.description,
    start: new Date(apiEvent.start_datetime),
    end: new Date(apiEvent.end_datetime),
    allDay: apiEvent.all_day,
    
    isLocked: apiEvent.is_locked,
    lockedBy: apiEvent.locked_by_data,
    lockedAt: apiEvent.locked_at,
    lockReason: apiEvent.lock_reason,
    
    category: apiEvent.category_data,
    categoryName: apiEvent.category_name,
    categoryColor: apiEvent.category_color,
    colorScheme: apiEvent.color_scheme,
    priority: apiEvent.priority,
    
    relatedOrder: apiEvent.related_order,
    relatedJob: apiEvent.related_job,
    
    createdBy: apiEvent.created_by_data,
    notes: apiEvent.notes,
    isActive: apiEvent.is_active,
    createdAt: apiEvent.created_at,
    updatedAt: apiEvent.updated_at,
    
    duration: apiEvent.duration,
    status: apiEvent.status,
  };
};

/**
 * Convert frontend CalendarEvent to API format
 */
export const transformToAPIEvent = (event: Partial<CalendarEvent>) => {
  return {
    title: event.title,
    description: event.description,
    start_datetime: event.start?.toISOString(),
    end_datetime: event.end?.toISOString(),
    all_day: event.allDay,
    category: event.category?.id,
    color_scheme: event.colorScheme,
    priority: event.priority,
    is_locked: event.isLocked,
    lock_reason: event.lockReason,
    related_order: event.relatedOrder,
    related_job: event.relatedJob,
    notes: event.notes,
    is_active: event.isActive,
  };
};

/**
 * Get date range for a given view and date
 */
export const getDateRangeForView = (date: Date, view: CalendarView): DateRange => {
  switch (view) {
    case 'month':
      return {
        start: startOfWeek(startOfMonth(date)),
        end: endOfWeek(endOfMonth(date)),
      };
    case 'week':
      return {
        start: startOfWeek(date),
        end: endOfWeek(date),
      };
    case 'day':
      return {
        start: date,
        end: date,
      };
    case 'agenda':
      // Show 30 days for agenda view
      return {
        start: date,
        end: addDays(date, 30),
      };
    default:
      return { start: date, end: date };
  }
};

/**
 * Navigate to next period based on view
 */
export const getNextDate = (date: Date, view: CalendarView): Date => {
  switch (view) {
    case 'month':
      return addMonths(date, 1);
    case 'week':
      return addWeeks(date, 1);
    case 'day':
      return addDays(date, 1);
    case 'agenda':
      return addDays(date, 30);
    default:
      return date;
  }
};

/**
 * Navigate to previous period based on view
 */
export const getPreviousDate = (date: Date, view: CalendarView): Date => {
  switch (view) {
    case 'month':
      return addMonths(date, -1);
    case 'week':
      return addWeeks(date, -1);
    case 'day':
      return addDays(date, -1);
    case 'agenda':
      return addDays(date, -30);
    default:
      return date;
  }
};

/**
 * Format date for display in header
 */
export const formatHeaderDate = (date: Date, view: CalendarView): string => {
  switch (view) {
    case 'month':
      return format(date, 'MMMM yyyy');
    case 'week': {
      const start = startOfWeek(date);
      const end = endOfWeek(date);
      return `${format(start, 'MMM dd')} – ${format(end, 'MMM dd, yyyy')}`;
    }
    case 'day':
      return format(date, 'EEEE, MMMM dd, yyyy');
    case 'agenda':
      return format(date, 'MMMM yyyy');
    default:
      return format(date, 'MMMM yyyy');
  }
};

/**
 * Sort events by priority (descending) then by start time
 */
export const sortEventsByPriority = (events: CalendarEvent[]): CalendarEvent[] => {
  return [...events].sort((a, b) => {
    // First sort by priority (higher priority first)
    if (a.priority !== b.priority) {
      return b.priority - a.priority;
    }
    // Then sort by start time
    return a.start.getTime() - b.start.getTime();
  });
};

/**
 * Get events for a specific date
 */
export const getEventsForDate = (events: CalendarEvent[], date: Date): CalendarEvent[] => {
  return events.filter(event => {
    // Check if event occurs on this date
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end);
    
    return (
      isSameDay(eventStart, date) ||
      isSameDay(eventEnd, date) ||
      (eventStart < date && eventEnd > date)
    );
  });
};

/**
 * Group events by date
 */
export const groupEventsByDate = (events: CalendarEvent[]): EventGroup[] => {
  const grouped = events.reduce((acc, event) => {
    const dateKey = format(event.start, 'yyyy-MM-dd');
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(event);
    return acc;
  }, {} as Record<string, CalendarEvent[]>);

  return Object.entries(grouped)
    .map(([date, events]) => ({
      date,
      events: sortEventsByPriority(events),
      count: events.length,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

/**
 * Check if event is locked
 */
export const isEventLocked = (event: CalendarEvent): boolean => {
  return event.isLocked === true;
};

/**
 * Check if event can be edited (not locked or user has permission)
 */
export const canEditEvent = (event: CalendarEvent, _userId?: string): boolean => {
  if (!event.isLocked) return true;
  // In Phase 1, we just check if locked
  // In future phases, check user permissions
  return false;
};

/**
 * Get event duration in hours
 */
export const getEventDuration = (event: CalendarEvent): number => {
  const durationMs = event.end.getTime() - event.start.getTime();
  return durationMs / (1000 * 60 * 60); // Convert to hours
};

/**
 * Format event time range
 */
export const formatEventTimeRange = (event: CalendarEvent): string => {
  if (event.allDay) {
    return 'All Day';
  }
  return `${format(event.start, 'hh:mm a')} – ${format(event.end, 'hh:mm a')}`;
};

/**
 * Get event status badge color
 */
export const getEventStatusColor = (status?: string): string => {
  switch (status) {
    case 'past':
      return 'gray';
    case 'ongoing':
      return 'green';
    case 'future':
      return 'blue';
    default:
      return 'gray';
  }
};

/**
 * Check if day has too many events (high density)
 */
export const isHighDensityDay = (eventCount: number, threshold: number = 30): boolean => {
  return eventCount >= threshold;
};

/**
 * Generate date key for date tracking
 */
export const getDateKey = (date: Date): string => {
  return format(date, 'yyyy-MM-dd');
};

/**
 * Parse date key back to Date
 */
export const parseDateKey = (dateKey: string): Date => {
  return new Date(dateKey);
};

/**
 * Get visible events (respecting maxVisible limit)
 */
export const getVisibleEvents = (
  events: CalendarEvent[],
  maxVisible: number
): { visible: CalendarEvent[]; hidden: number } => {
  const sorted = sortEventsByPriority(events);
  return {
    visible: sorted.slice(0, maxVisible),
    hidden: Math.max(0, sorted.length - maxVisible),
  };
};

/**
 * Filter events by search query
 */
export const filterEventsByQuery = (events: CalendarEvent[], query: string): CalendarEvent[] => {
  if (!query.trim()) return events;
  
  const lowerQuery = query.toLowerCase();
  return events.filter(event => 
    event.title.toLowerCase().includes(lowerQuery) ||
    event.description?.toLowerCase().includes(lowerQuery)
  );
};

/**
 * Filter events by category
 */
export const filterEventsByCategory = (events: CalendarEvent[], categoryId?: string): CalendarEvent[] => {
  if (!categoryId) return events;
  return events.filter(event => event.category?.id === categoryId);
};

/**
 * Filter events by lock status
 */
export const filterEventsByLockStatus = (events: CalendarEvent[], isLocked?: boolean): CalendarEvent[] => {
  if (isLocked === undefined) return events;
  return events.filter(event => event.isLocked === isLocked);
};

/**
 * Get statistics for events
 */
export const getEventStatistics = (events: CalendarEvent[]) => {
  return {
    total: events.length,
    locked: events.filter(e => e.isLocked).length,
    unlocked: events.filter(e => !e.isLocked).length,
    allDay: events.filter(e => e.allDay).length,
    timed: events.filter(e => !e.allDay).length,
    past: events.filter(e => e.status === 'past').length,
    ongoing: events.filter(e => e.status === 'ongoing').length,
    future: events.filter(e => e.status === 'future').length,
  };
};

/**
 * Transform Job to CalendarEvent
 * Converts backend Job model to calendar event format for display
 * 
 * IMPORTANT: Uses the actual time from potential_delivery_date for sequencing.
 * The time determines the order of jobs on the same day.
 */
export const transformJobToCalendarEvent = (job: any): CalendarEvent => {
  const deliveryDate = job.potential_delivery_date 
    ? new Date(job.potential_delivery_date) 
    : new Date();
  
  // Use the actual time from potential_delivery_date for sequencing
  // This is critical for the time-based sequencing system
  const startDate = new Date(deliveryDate);
  
  // End time is 5 minutes after start (one time slot)
  const endDate = new Date(deliveryDate);
  endDate.setMinutes(endDate.getMinutes() + 5);
  
  return {
    id: job.id,
    title: `${job.delivery_postcode} - #${job.website_job_number}`,
    description: `${job.customer_first_name} ${job.customer_last_name}\n${job.customer_email || 'No email'}\n${job.customer_mobile_number || 'No phone'}`,
    start: startDate,
    end: endDate,
    allDay: false, // Must be false for time-based sequencing
    colorScheme: 'delivery', // All blue for Phase 2
    priority: job.priority === 'urgent' ? 3 : (job.priority === 'high' ? 2 : 1),
    isLocked: false,
    isActive: true,
    createdAt: job.created_at,
    updatedAt: job.updated_at,
    status: 'future',
    
    // Store job metadata for display and actions
    notes: {
      jobId: job.id,
      postcode: job.delivery_postcode,
      orderNumber: job.website_job_number,
      customerName: `${job.customer_first_name} ${job.customer_last_name}`,
      customerEmail: job.customer_email || '',
      customerPhone: job.customer_mobile_number || '',
      fulfillmentType: job.fulfillment_type,
      jobStatus: job.status,
      totalAmount: job.total_amount,
      // Additional fields for expanded view
      createdAt: job.created_at,
      updatedAt: job.updated_at,
      deliveryNotes: job.delivery_notes,
      deliveryAddressLine1: job.delivery_address_line1,
      deliveryAddressLine2: job.delivery_address_line2,
      customerCompany: job.customer_company,
      // Production and admin fields
      glassArrivalDate: job.glass_arrival_date,
      manufactureDate: job.manufacture_date,
      totalItems: job.total_items,
      productionNotes: job.production_notes,
      adminNotes: job.admin_notes,
      // Delivery user information
      deliveryUserId: job.delivery_user,
      deliveryUserName: job.delivery_user_username,
      deliveryUserData: job.delivery_user_data, // Include full user object with color
      // Include full job object for lock data access
      job: job,
    },
  };
};

/**
 * Format currency in GBP
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(amount);
};

/**
 * Get status badge color based on job status
 */
export const getStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    new: 'bg-blue-100 text-blue-800 border-blue-300',
    in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    ready_for_delivery: 'bg-green-100 text-green-800 border-green-300',
    delivered: 'bg-purple-100 text-purple-800 border-purple-300',
    completed: 'bg-gray-100 text-gray-800 border-gray-300',
    cancelled: 'bg-red-100 text-red-800 border-red-300',
  };
  return statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
};

/**
 * Get status display name
 */
export const getStatusDisplayName = (status: string): string => {
  const statusNames: Record<string, string> = {
    new: 'New',
    in_progress: 'In Progress',
    ready_for_delivery: 'Ready for Delivery',
    delivered: 'Delivered',
    completed: 'Completed',
    cancelled: 'Cancelled',
  };
  return statusNames[status] || status;
};

